import { Auth0Client } from '@auth0/auth0-spa-js';
import * as Storage from './storage';

// Where this UI is actually served. Core mounts the SPA under a path (`/ui/`)
// and serves the public player page at the origin root, so a callback that
// returns to the bare origin lands on the player, not the app -- and the
// authorization code is never consumed. Always derive the redirect from the
// page we are running on; never from the bare origin.
const appBaseUri = () => window.location.origin + window.location.pathname.replace(/[^/]*$/, '');

// Auth0 tenant baked in at build time (Create React App only substitutes
// REACT_APP_* during `yarn build`). These are the fallback for a deployment
// whose login page cannot read the tenant list from Core: /about and /config
// both require a JWT, so an unauthenticated page has nothing to build its
// Auth0 client from -- which is what produced an /authorize request with an
// empty client_id ("invalid_request: Missing required parameter: client_id").
// A client id and audience are public values by design, not secrets.
const DEFAULTS = {
	domain: process.env.REACT_APP_AUTH0_DOMAIN || '',
	client_id: process.env.REACT_APP_AUTH0_CLIENT_ID || '',
	audience: process.env.REACT_APP_AUTH0_AUDIENCE || '',
};

let client = null;

let isAvailable = false;

try {
	new Auth0Client({
		domain: 'example.eu.auth0.com',
		clientId: 'some_client_id',
		authorizationParams: {
			redirect_uri: 'https://example.com/',
			audience: 'https://example.com/',
		},
		cacheLocation: 'memory',
	});

	isAvailable = true;
} catch (e) {
	isAvailable = false;
}

const canUseAuth0 = () => {
	return isAvailable;
};

const setConfig = (config) => {
	Storage.Set(
		'auth0',
		JSON.stringify({
			domain: '',
			client_id: '',
			audience: '',
			redirect_uri: window.location.origin,
			...config,
		})
	);
};

const getStoredConfig = () => {
	let config = {
		domain: '',
		client_id: '',
		audience: '',
		redirect_uri: window.location.origin,
	};

	const data = Storage.Get('auth0');
	if (data === null) {
		return config;
	}

	try {
		let parsedConfig = JSON.parse(data);

		config = {
			...config,
			...parsedConfig,
		};
	} catch (e) {}

	return config;
};

// Anything the stored config leaves blank falls back to the build-time tenant,
// so a fresh browser with no stored config still gets a usable Auth0 client
// (and Login.js can offer Auth0 as a login target).
const getConfig = () => {
	const stored = getStoredConfig();

	return {
		domain: stored.domain.length !== 0 ? stored.domain : DEFAULTS.domain,
		client_id: stored.client_id.length !== 0 ? stored.client_id : DEFAULTS.client_id,
		audience: stored.audience.length !== 0 ? stored.audience : DEFAULTS.audience,
		// Never taken from storage: the callback has to come back to wherever
		// this UI is mounted, and a stored value from an earlier build (or an
		// earlier deployment) would send it somewhere else entirely.
		redirect_uri: appBaseUri(),
	};
};

const init = () => {
	if (canUseAuth0() === false) {
		return false;
	}

	const config = getConfig();

	if (config.domain.length === 0 || config.client_id.length === 0 || config.audience.length === 0) {
		return false;
	}

	try {
		// @auth0/auth0-spa-js v2 renamed client_id to clientId and moved
		// audience/redirect_uri/scope under authorizationParams. Built the v1
		// way (client_id, audience at the top level) the SDK silently drops
		// all of them: the client keeps only its domain, and the /authorize
		// URL it produces carries the SDK defaults (state, nonce, PKCE,
		// scope) but no client_id, redirect_uri or audience -- which Auth0
		// rejects with "invalid_request: Missing required parameter:
		// client_id".
		client = new Auth0Client({
			domain: config.domain,
			clientId: config.client_id,
			authorizationParams: {
				redirect_uri: config.redirect_uri,
				audience: config.audience,
				scope: 'openid profile email',
			},
			cacheLocation: 'localstorage',
		});
	} catch (e) {
		return false;
	}

	return true;
};

const handleRedirectCallback = async () => {
	if (client === null) {
		return {
			initialized: false,
		};
	}

	const urlParams = new URLSearchParams(window.location.search.substring(1));
	if (urlParams.has('error')) {
		return {
			initialized: true,
			error: true,
			description: urlParams.has('error_description') ? urlParams.get('error_description') : 'unknown error',
		};
	}

	if (urlParams.has('code') && urlParams.has('state')) {
		try {
			await client.handleRedirectCallback();
		} catch (e) {
			return {
				initialized: true,
				error: true,
				description: e.message,
			};
		}

		urlParams.delete('code');
		urlParams.delete('state');

		let hash = urlParams.get('hash');
		urlParams.delete('hash');

		let href = window.location.origin + window.location.pathname + '?' + urlParams.toString();
		if (hash !== null) {
			href += '#' + hash;
		}

		window.location.href = href;
	}

	return {
		initialized: true,
		error: false,
	};
};

const login = async (queryParams) => {
	if (client === null) {
		return false;
	}

	// v2 carries extra login state in appState. The v1 idiom this code was
	// written for -- appending the extras to redirect_uri -- builds a callback
	// URL that no longer matches the application's registered Allowed Callback
	// URLs, so Auth0 rejects it before ever showing a login form.
	const options = {
		authorizationParams: {
			redirect_uri: appBaseUri(),
		},
		appState: queryParams,
	};

	try {
		await client.loginWithRedirect(options);
	} catch (e) {
		return false;
	}

	return true;
};

const logout = async () => {
	if (client === null) {
		return;
	}

	// v2 moved returnTo under logoutParams and dropped localOnly. openUrl is
	// left at its default (true) on purpose: with false the SDK clears only its
	// own cache and leaves Auth0's session cookie standing, so the app
	// re-authenticates from it during the very next validation and the user
	// lands back inside the UI logged in -- which is exactly what "logout" must
	// not do. Leaving it true sends the browser through Auth0's /v2/logout,
	// ending the session there as well; the app then comes back to its login
	// screen. returnTo is the mount path without query or hash, which is what
	// the application's Allowed Logout URLs match against.
	await client.logout({
		logoutParams: { returnTo: appBaseUri() },
	});
};

const getToken = async () => {
	let token = '';

	if (client === null) {
		return token;
	}

	try {
		token = await client.getTokenSilently();
	} catch (error) {
		console.error(error);
	}

	return token;
};

const isAuthenticated = async () => {
	if (client === null) {
		return false;
	}

	return await client.isAuthenticated();
};

export { canUseAuth0, setConfig, getConfig, init, login, logout, isAuthenticated, getToken, handleRedirectCallback };
